// @flow
import type { ActivityLogEvent, ActivityLogData, EntityInfo } from '../types'
import React, { Component } from 'react'
import LiveSelect from 'live/components/forms/ui/select'
import { Radio } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import MaskedInput from 'react-maskedinput'
import CustomDateInput from 'live/components/forms/ui/custom-react-datepicker-input'
import styled from 'styled-components'

import TagsInput from 'react-tagsinput'
import moment from 'moment'
import _ from 'lodash'
import DateService from 'live/services/date'
import i18n from 'live/services/i18n'
import theme from 'live/services/theme'
import Select from 'live/components/forms/ui/select'
import globalNotification from 'live/services/global-notification'
import FieldError from 'live/components/forms/ui/field-error'
import Utils from 'live/lib/utils'

import {
    fetchCSDs,
    fetchActivities,
    fetchInterventions,
    fetchInitiatives,
    fetchClientFunctions,
    fetchDetailing,
    fetchDetailingFor,
    fetchServiceNatureList,
    fetchSmsGainList,
    fetchPerformanceGainList,
    fetchWells,
    fetchUsers,
    createOrUpdateActivityLog,
    fetchAssetRigs,
    fetchCsdFromUser
} from '../api'

type ErrorMap = Map<string, boolean>
type SelectOption = { label: string, value: string, data?: Object } | void
type Props = {
    event?: ActivityLogEvent,
    liftSubmitFn?: (fn: () => Promise<*>) => void,
    onHide: Function,
    showActions: boolean
}
type State = {
    errors: Map<string, boolean>,
    beginDate: typeof moment,
    beginTime: string,
    endDate: typeof moment,
    endTime: string,
    csds: [],
    activities: [],
    interventions: [],
    initiatives: [],
    clientRoles: [],
    detailing1List: [],
    detailing2List: [],
    serviceNatureList: [],
    smsGainList: [],
    performanceGainList: [],
    wells: [],
    rigs: [],
    clients: [],
    activityLog: ActivityLogData
}

class ActivityLogForm extends Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            errors: new Map(),
            beginDate: moment(), // date input form value
            beginTime: moment().format('HH:mm:ss'), // time input form value
            endDate: null,
            endTime: '', // time input form value
            csds: [],
            activities: [],
            interventions: [],
            initiatives: [],
            clientRoles: [],
            detailing1List: [],
            detailing2List: [],
            serviceNatureList: [],
            smsGainList: [],
            performanceGainList: [],
            wells: [],
            rigs: [],
            clients: [],
            activityLog: {
                uid: null,
                authorId: null,
                author: null,
                well: null,
                rig: null,
                csd: null,
                begin: +new Date(),
                end: null,
                activity: null,
                intervention: null,
                initiative: null,
                client: null,
                clientRole: null,
                detailing1: null,
                detailing2: null,
                serviceNature: null,
                smsGain: null,
                performanceGain: null,
                tags: [],
                comments: ''
            }
        }
    }

    componentDidMount() {
        if (this.props.event) this.populateFromEvent({ ...this.props.event })
        else this.populate()

        if (_.isFunction(this.props.liftSubmitFn)) this.props.liftSubmitFn(this.onSubmit)
    }

    /**
     * Gets list heads if event was created
     * This is needed because react-select needs the value to be on the option list
     */
    getHeadIfCreated(id: number | string, value: Object): [] | [Object] {
        if (isNaN(+id)) return [value]

        return []
    }

    populateFromEvent(event: ActivityLogEvent) {
        const rigName = _.get(event, 'rig.name', event.rig)
        const wellName = _.get(event, 'well.name', event.rig)
        const client = event.client || {}

        const rigs = rigName ? [{ value: rigName, label: rigName }] : []
        const wells = wellName ? [{ value: wellName, label: wellName }] : []

        const clients = this.getHeadIfCreated(client.id, {
            value: client.id || client.name,
            label: client.name
        })

        // only form values that are outside activityLog object
        const beginDate = DateService.getMoment(event.begin)
        const beginTime = beginDate.format('HH:mm:ss')

        const endDate = event.end ? DateService.getMoment(event.end) : null
        const endTime = event.end ? endDate.format('HH:mm:ss') : ''

        this.setState(
            {
                activityLog: {
                    ...this.state.activityLog,
                    ...event
                },
                beginDate,
                beginTime,
                endDate,
                endTime,
                rigs,
                wells,
                clients
            },
            () => this.populate()
        )
    }

    populate = () => {
        this.fecthWellList()
        this.fecthRigList()
        this.fecthCsdsList()
        this.fecthInterventions()
        this.fecthInitiatives()
        this.fecthClientFunctions()
        this.fecthDetailing1List()
        this.fecthServiceNatureList()
        this.fecthSmsGainList()
        this.fetchPerformanceGainList()
        this.fetchClients()
    }

    async fetchUserCsd() {
        const csd: string = (await fetchCsdFromUser().catch(() => null)) || 'outros'
        const foundCsd = this.state.csds.find(c => c.label.toLowerCase() === csd.toLowerCase())

        if (!csd) return console.warn('Usuário não encontrado na lookup table Usuários X CSDs')

        if (!foundCsd) {
            return console.warn('CSD listado na lookup table não foi encontrado no JSON definido na administração.')
        }

        const csdEntity = {
            id: foundCsd.value,
            name: foundCsd.label
        }

        this.updateActivityLog('csd', csdEntity)
        this.fecthActivitiesFor(csdEntity)
    }

    fecthCsdsList = () => {
        fetchCSDs().then(picklist => {
            let csds = makeSelectList(picklist)

            this.setState({ csds }, () => {
                if (this.state.activityLog.csd) this.fecthActivitiesFor(this.state.activityLog.csd)
                else this.fetchUserCsd()
            })
        })
    }

    fecthActivitiesFor = (csdOption: EntityInfo) => {
        fetchActivities(csdOption.id).then(picklist => {
            let activities = makeSelectList(picklist)

            this.setState({
                activities: activities
            })
        })
    }

    fecthInterventions() {
        fetchInterventions().then(picklist => {
            this.setState({
                interventions: makeSelectList(picklist)
            })
        })
    }

    fecthInitiatives() {
        fetchInitiatives().then(picklist => {
            let initiatives = makeSelectList(picklist)
            let selectedInitiative = this.state.activityLog.initiative

            if (!selectedInitiative && initiatives.length > 0) {
                selectedInitiative = optionValueExtractor(initiatives[0])
                // csd now is the only initiative (it is the first of the list)
                this.updateActivityLog('initiative', selectedInitiative)
            }
        })
    }

    fecthClientFunctions() {
        fetchClientFunctions().then(picklist => {
            let clientRoles = makeSelectList(picklist)

            this.setState({
                clientRoles
            })
        })
    }

    fecthDetailing1List = () => {
        fetchDetailing().then(picklist => {
            let detailing1List = makeSelectList(picklist)
            let selectedDetail = this.state.activityLog.detailing1

            this.setState({ detailing1List })

            if (selectedDetail) this.fecthDetailingFor(selectedDetail)
        })
    }

    fecthDetailingFor = (detailing1: EntityInfo) => {
        fetchDetailingFor(detailing1.id).then(picklist => {
            let details = makeSelectList(picklist)
            let selectedDetail = this.state.activityLog.detailing2

            if (!selectedDetail && details.length > 0) selectedDetail = optionValueExtractor(details[0])

            this.setState({
                detailing2List: details
            })
        })
    }

    fecthServiceNatureList = () => {
        fetchServiceNatureList().then(picklist => {
            let snlist = makeSelectList(picklist)
            let selectedService = this.state.activityLog.serviceNature

            if (!selectedService && snlist.length > 0) selectedService = optionValueExtractor(snlist[0])

            this.setState({
                serviceNatureList: snlist
            })
        })
    }

    fecthSmsGainList = () => {
        fetchSmsGainList().then(picklist => {
            let selectedGain = this.state.activityLog.smsGain

            if (!selectedGain && picklist.length > 0) selectedGain = picklist[0]

            this.setState({
                smsGainList: picklist
            })
        })
    }

    fetchPerformanceGainList = () => {
        fetchPerformanceGainList().then(picklist => {
            let selectedGain = this.state.activityLog.performanceGain

            if (!selectedGain && picklist.length > 0) selectedGain = picklist[0]

            this.setState({
                performanceGainList: picklist
            })
        })
    }

    fecthWellList = cb => {
        fetchWells().then(response => {
            let wells = [
                ...response.map(w => ({
                    value: w.name,
                    label: w.name,
                    data: {
                        id: w.name,
                        name: w.name
                    }
                }))
            ]

            this.setState({ wells }, cb)
        })
    }

    fecthRigList = () => {
        fetchAssetRigs().then((picklist: AssetEntity) => {
            let rigs = _.uniqBy([...this.state.rigs, ...makeSelectListFromRigs(picklist)], 'label')

            this.setState({ rigs })
        })
    }

    fetchClients = () => {
        fetchUsers().then(({ data: users }) => {
            this.setState({
                clients: _.uniqBy(
                    [
                        ...this.state.clients,
                        ...users.map(user => {
                            const userObject = _.pick(user, 'id', 'name', 'username', 'email')

                            userObject.name = user.name || user.username

                            return {
                                label: `${userObject.name} (${user.username})`,
                                value: userObject.id,
                                data: userObject
                            }
                        })
                    ],
                    'value'
                )
            })
        })
    }

    // onChange handlers
    updateActivityLog(field, value) {
        this.setState(
            {
                activityLog: {
                    ...this.state.activityLog,
                    [field]: value
                }
            },
            this.updateValidation
        )
    }

    updateValidation = _.throttle(this._updateValidation.bind(this), 300)

    _updateValidation() {
        if (this.state.errors.size) this.setState({ errors: this.validate(this.state.activityLog) })
    }

    // high order fns
    onChangeActivityLogField = (field: string, valueExtractor: * = _.identity) => (...args: *) =>
        this.updateActivityLog(field, valueExtractor(...args))
    onChangeSelectWithData = (field: string) =>
        this.onChangeActivityLogField(field, (option: SelectOption, opt) => this.getEntityFromOption(opt || option))

    onSelectRig = (v, option) => {
        const rig = option && _.get(this.state.rigs.find(rig => rig.value === option.value), 'data')

        if (rig) {
            const newState = {
                wells: this.state.wells.slice(),
                activityLog: {
                    ...this.state.activityLog,
                    rig: {
                        id: rig.name,
                        name: rig.name
                    }
                }
            }

            const wellId = _.get(rig, 'state.well.data.id')
            const wellName = _.get(rig, 'state.well_name.data')
            const intervention = _.get(rig, 'state.intervention.data')

            if (wellName) {
                newState.activityLog.well = {
                    id: wellName,
                    name: wellName
                }
            }

            if (intervention) {
                const interventionFromList = this.state.interventions.find(
                    i => i.value.toLowerCase() === i18n(intervention.toLowerCase()).toLowerCase() || i.label.toLowerCase() === i18n(intervention.toLowerCase()).toLowerCase()
                )

                if (interventionFromList) {
                    newState.activityLog.intervention = {
                        id: interventionFromList.data.id,
                        name: interventionFromList.label
                    }
                }
            }

            if (wellName && !newState.wells.find(w => w.value === wellName)) {
                newState.wells.push({
                    value: wellName,
                    label: wellName,
                    data: {
                        id: wellName,
                        name: wellName
                    }
                })
            }

            this.setState(newState, this.updateValidation)
        }
    }

    getEntityFromOption = (option: *) => {
        if (!option) return null

        if (option.data) return option.data

        return {
            id: option.value,
            name: option.value
        }
    }

    onChangeCSD = (v, option) => {
        let csd = optionValueExtractor(option)

        this.setState(
            {
                activityLog: {
                    ...this.state.activityLog,
                    csd: csd,
                    activity: null
                }
            },
            () => {
                this.fecthActivitiesFor(csd)
                this.updateValidation()
            }
        )
    }

    onChangeDetailing1 = (v, option) => {
        let detailing = optionValueExtractor(option)

        this.setState(
            {
                activityLog: {
                    ...this.state.activityLog,
                    detailing1: detailing,
                    detailing2: null
                }
            },
            () => {
                this.updateValidation()
                this.fecthDetailingFor(detailing)
            }
        )
    }

    onChangeBeginDate = (beginDate: typeof moment) => {
        const beginTime = getUnixFromDateAndTime(beginDate, this.state.beginTime)

        this.setState({ beginDate }, this.updateValidation)
        this.updateActivityLog('begin', beginTime)
    }

    onChangeBeginTime = e => {
        const beginTime = e.target.value
        const timeValue = beginTime ? beginTime.replace(/_/, '0') : '00:00:00'
        const curTime = getUnixFromDateAndTime(this.state.beginDate, timeValue)

        this.setState({ beginTime }, this.updateValidation)
        this.updateActivityLog('begin', curTime)
    }

    onChangeEndDate = (endDate: typeof moment) => {
        const endTime = endDate ? getUnixFromDateAndTime(endDate, this.state.endTime) : null

        this.setState({ endDate }, this.updateValidation)
        this.updateActivityLog('end', endTime)
    }

    onChangeEndTime = e => {
        if (!this.state.endDate) this.setState({ endDate: this.state.beginDate })

        const endTime = e.target.value
        const timeValue = endTime ? endTime.replace(/_/, '0') : '00:00:00'
        const curTime = endTime ? getUnixFromDateAndTime(this.state.endDate, timeValue) : null

        this.setState({ endTime }, this.updateValidation)
        this.updateActivityLog('end', curTime)
    }

    isValidEntity = (obj: Object) => _.isString(_.get(obj, 'name'))

    getNotNumbers = (obj: Object) =>
        _.reduce(obj, (acc: ErrorMap, value, field) => (!_.isNumber(value) ? acc.set(field, true) : acc), new Map())

    getNotStrings = (obj: Object) =>
        _.reduce(
            obj,
            (acc: ErrorMap, value, field) => (!value || !_.isString(value) ? acc.set(field, true) : acc),
            new Map()
        )

    getInvalidEntities = (obj: Object) =>
        _.reduce(obj, (acc, value, field) => (!this.isValidEntity(value) ? acc.set(field, true) : acc), new Map())

    beginDateAfterEnd = ({ begin, end }) => {
        const m = new Map()

        if (end && begin > end) m.set('begin-date-after-end', true)

        return m
    }

    endDateWithoutEndTime = ({ end }) => {
        const m = new Map()
        let { endTime, endDate } = this.state

        if (endDate && !endTime) m.set('end-date-without-end-time', true)

        return m
    }

    validate = (activityLog: ActivityLogData): ErrorMap => {
        const listFields = ['rig', 'well', 'intervention', 'csd', 'activity', 'smsGain', 'performanceGain']

        return new Map([
            ...this.beginDateAfterEnd(_.pick(activityLog, 'begin', 'end')),
            ...this.getNotStrings(_.pick(activityLog, 'comments')),
            ...this.getNotNumbers(_.pick(activityLog, 'begin')),
            ...this.endDateWithoutEndTime(_.pick(activityLog, 'end')),
            ...this.getInvalidEntities(_.pick(activityLog, ...listFields))
        ])
    }

    onSubmit = () => {
        const errors = this.validate(this.state.activityLog)
        const queryParams = Utils.getQueryParams()

        this.setState({ errors })

        return new Promise((resolve, reject) => {
            if (errors.size) return reject(errors)
            const { dashboardId } = this.props
            const activityLog = this.state.activityLog

            const data = {
                ...activityLog,
                dashboardId,
                dashboardSpan: activityLog.dashboardSpan || (queryParams ? queryParams['span'] : undefined)
            }

            createOrUpdateActivityLog(data)
                .then(response => {
                    const succesMessage =
                        this.props.event && this.props.event.uid
                            ? i18n('REGISTRO DE ATIVIDADE atualizado com sucesso.')
                            : i18n('Registro de atividade criado com sucesso.')

                    globalNotification.success(succesMessage, 4)

                    if (_.isFunction(this.props.onHide)) this.props.onHide(data)

                    resolve(response)
                })
                .catch(r => {
                    globalNotification.error(
                        `${i18n('Erro ao salvar o registro de atividades')}  ${
                            r.statusText ? `- (${r.statusText})` : ''
                        }`
                    )

                    reject(r)
                })
        })
    }

    render() {
        const {
            csds,
            activityLog,
            activities,
            interventions,
            initiatives,
            clientRoles,
            detailing1List,
            detailing2List,
            serviceNatureList,
            smsGainList,
            performanceGainList,
            beginDate,
            beginTime,
            endDate,
            endTime,
            wells,
            rigs,
            clients,
            errors
        } = this.state

        const { showActions } = this.props

        return (
            <ActivityLogFormContainer>
                <form name="activity-log-form" className="form-body" onSubmit={e => e.preventDefault()}>
                    <Section>
                        <SectionTitle>{i18n('Basic information').toUpperCase()}</SectionTitle>

                        <Field>
                            <Label>{i18n('Rig')}</Label>
                            <LiveSelect
                                placeholder={i18n('Choose')}
                                promptTextCreator={promptCreator}
                                onChange={this.onSelectRig}
                                value={_.get(activityLog.rig, 'name')}
                                options={rigs}
                                style={errors.get('rig') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('rig')} type="required" />
                        </Field>
                        <Field>
                            <Label>{i18n('Well')}</Label>
                            <LiveSelect
                                creatable
                                placeholder={i18n('Choose')}
                                promptTextCreator={promptCreator}
                                onChange={this.onChangeSelectWithData('well')}
                                value={_.get(activityLog.well, 'name')}
                                options={wells}
                                style={errors.get('well') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('well')} type="required" />
                        </Field>
                        <Field>
                            <div className="flex flex-space-between activity-log-form-date-picker">
                                <div className="flex-2 mr-5">
                                    <Label>{i18n('Begin date')}</Label>
                                    <ActivityLogDatePicker
                                        customInput={<CustomDateInput />}
                                        selected={beginDate}
                                        onChange={this.onChangeBeginDate}
                                        dateFormat={DateService.getDateFormat()}
                                        placeholderText={DateService.getDateFormat().toLowerCase()}
                                    />
                                </div>
                                <div className="flex-1 ml-5 timepicker-box">
                                    <Label>{i18n('Hour')}</Label>
                                    <MaskedInput
                                        value={beginTime}
                                        onChange={this.onChangeBeginTime}
                                        type="text"
                                        mask="11:11:11"
                                    />
                                </div>
                            </div>
                            <FieldError show={errors.get('begin')} type="required" />
                        </Field>
                        <Field>
                            <div className="flex flex-space-between activity-log-form-date-picker">
                                <div className="flex-2 mr-5">
                                    <Label>
                                        {i18n('End date')} <span>({i18n('optional')})</span>:
                                    </Label>
                                    <ActivityLogDatePicker
                                        customInput={<CustomDateInput />}
                                        autoComplete="false"
                                        selected={endDate}
                                        onChange={this.onChangeEndDate}
                                        dateFormat={DateService.getDateFormat()}
                                        placeholderText={DateService.getDateFormat().toLowerCase()}
                                        openToDate={beginDate}
                                        minDate={beginDate}
                                    />
                                </div>
                                <div className="flex-1 ml-5 timepicker-box">
                                    <Label>
                                        {i18n('Hour')} <span>({i18n('optional')})</span>
                                    </Label>
                                    <MaskedInput
                                        value={endTime}
                                        onChange={this.onChangeEndTime}
                                        type="text"
                                        mask="11:11:11"
                                    />
                                </div>
                            </div>
                            <FieldError
                                message={i18n('The end date must be after the start date')}
                                show={errors.get('begin-date-after-end')}
                                type="required"
                            />
                            <FieldError
                                message={i18n('The end time must be selected when choosing an end date')}
                                show={errors.get('end-date-without-end-time')}
                                type="required"
                            />
                        </Field>
                        <Field>
                            <Label>{i18n('Intervention')}</Label>
                            <Select
                                placeholder={i18n('Choose')}
                                onChange={this.onChangeActivityLogField('intervention', liveOnChangeSelectExtractor)}
                                value={_.get(activityLog.intervention, 'id')}
                                clearable={false}
                                options={interventions}
                                style={errors.get('intervention') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('intervention')} type="required" />
                        </Field>
                    </Section>
                    <Section>
                        <SectionTitle>{i18n('Classification').toUpperCase()}</SectionTitle>
                        {/* <Field>
                            <Label>{i18n('Iniciativa')}</Label>
                            <Select
                                placeholder="Escolha"
                                value={_.get(activityLog.initiative, 'id')}
                                onChange={this.onChangeActivityLogField('initiative', liveOnChangeSelectExtractor)}
                                options={initiatives}
                                clearable={false}
                                style={errors.get('initiative') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('initiative')} type="required" />
                        </Field> */}
                        <Field>
                            <Label>{i18n('Posto CSD')}</Label>
                            <Select
                                placeholder={i18n('Choose')}
                                value={_.get(activityLog.csd, 'id')}
                                onChange={this.onChangeCSD}
                                options={csds}
                                clearable={false}
                                style={errors.get('csd') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('csd')} type="required" />
                        </Field>
                        <Field>
                            <Label>{i18n('Activity')}</Label>
                            <Select
                                placeholder={i18n('Choose')}
                                onChange={this.onChangeActivityLogField('activity', liveOnChangeSelectExtractor)}
                                value={_.get(activityLog.activity, 'id')}
                                options={activities}
                                disabled={!_.get(activityLog.csd, 'id')}
                                clearable={false}
                                style={errors.get('activity') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('activity')} type="required" />
                        </Field>

                        {/* <Field>
                            <Label>{i18n('Cliente')}</Label>
                            <Creatable
                                placeholder="Escolha"
                                promptTextCreator={promptCreator}
                                onChange={this.onChangeSelectWithData('client')}
                                options={clients}
                                value={_.get(activityLog.client, 'id', _.get(activityLog.client, 'name'))}
                                style={errors.get('client') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('client')} type="required" />
                        </Field> */}
                        <Field>
                            <Label>
                                {i18n('Client Role')} ({i18n('optional')})
                            </Label>
                            <Select
                                placeholder={i18n('Choose')}
                                value={_.get(activityLog.clientRole, 'id')}
                                onChange={this.onChangeActivityLogField('clientRole', liveOnChangeSelectExtractor)}
                                options={clientRoles}
                                clearable={false}
                                style={errors.get('clientRole') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('clientRole')} type="required" />
                        </Field>
                        <Field>
                            <Label>
                                {i18n('Detailing 1')} ({i18n('optional')})
                            </Label>
                            <Select
                                placeholder={i18n('Choose')}
                                value={_.get(activityLog.detailing1, 'id')}
                                onChange={this.onChangeDetailing1}
                                options={detailing1List}
                                clearable={false}
                                style={errors.get('detailing1') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('detailing1')} type="required" />
                        </Field>

                        <Field>
                            <Label>
                                {i18n('Detailing 2')} ({i18n('optional')})
                            </Label>
                            <Select
                                placeholder={i18n('Choose')}
                                value={_.get(activityLog.detailing2, 'id')}
                                onChange={this.onChangeActivityLogField('detailing2', liveOnChangeSelectExtractor)}
                                options={detailing2List}
                                disabled={!_.get(activityLog.detailing1, 'id')}
                                clearable={false}
                                style={errors.get('detailing2') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('detailing2')} type="required" />
                        </Field>
                        <Field>
                            <Label>
                                {i18n('Service Nature')} ({i18n('optional')})
                            </Label>
                            <Select
                                placeholder={i18n('Choose')}
                                value={_.get(activityLog.serviceNature, 'id')}
                                onChange={this.onChangeActivityLogField('serviceNature', liveOnChangeSelectExtractor)}
                                options={serviceNatureList}
                                clearable={false}
                                style={errors.get('serviceNature') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('serviceNature')} type="required" />
                        </Field>
                    </Section>
                    <Section>
                        <SectionTitle>{i18n('Value generation estimate')}</SectionTitle>
                        <Field>
                            <Label>{i18n('Safety/Conformity')}</Label>
                            {smsGainList.map(sms => (
                                <Radio
                                    value={sms.id}
                                    onChange={this.onChangeActivityLogField('smsGain', () => sms)}
                                    key={sms.id}
                                    checked={_.get(activityLog.smsGain, 'id') === sms.id}
                                    name="smsGain"
                                >
                                    {sms.name}
                                </Radio>
                            ))}
                            <FieldError show={errors.get('smsGain')} type="required" />
                        </Field>

                        <Field>
                            <Label>{i18n('Performance/Financial')}</Label>
                            {performanceGainList.map(perf => (
                                <Radio
                                    value={perf.id}
                                    onChange={this.onChangeActivityLogField('performanceGain', () => perf)}
                                    key={perf.id}
                                    name="perfGain"
                                    checked={_.get(activityLog.performanceGain, 'id') === perf.id}
                                >
                                    {perf.name}
                                </Radio>
                            ))}
                            <FieldError show={errors.get('performanceGain')} type="required" />
                        </Field>
                    </Section>
                    <Section>
                        <SectionTitle>{i18n('Additional information').toUpperCase()}</SectionTitle>
                        <Field>
                            <Label className="control-label">
                                {i18n('Tags (opcional, crie com vírgula ou [enter])')}
                            </Label>
                            <TagsInput
                                name="tags"
                                addKeys={[9, 13, 188]}
                                addOnBlur={true}
                                value={activityLog.tags}
                                inputProps={{
                                    placeholder: i18n('Add tags')
                                }}
                                onChange={this.onChangeActivityLogField('tags')}
                            />
                            <span className="form-control-feedback glyphicon glyphicon-ok" />
                        </Field>

                        <Field>
                            <Label>{i18n('Comments')}</Label>
                            <CommentsArea
                                placeholder={i18n('Write')}
                                value={activityLog.comments}
                                onChange={this.onChangeActivityLogField('comments', eventValueExtractor)}
                                style={errors.get('comments') ? { border: borderError } : {}}
                            />
                            <FieldError show={errors.get('comments')} type="required" />
                        </Field>
                    </Section>
                    {showActions ? (
                        <Section>
                            <button className="btn btn-primary" onClick={this.onSubmit}>
                                {i18n('Save')}
                            </button>
                            {' ou '} <a onClick={this.props.onHide}>{i18n('Cancel').toLowerCase()}</a>
                        </Section>
                    ) : null}
                </form>
            </ActivityLogFormContainer>
        )
    }
}

class ActivityLogDatePicker extends DatePicker {
    // this is a suggested "fix" for the issue of not closing datepicker calendar when another
    // react select is cliked while datepicker calendar is still opened
    deferFocusInput = () => {
        this.cancelFocusInput()
    }
}

export default ActivityLogForm

const Field = styled.div`
    margin-bottom: 7px;

    .Select,
    .Select-control,
    input,
    textarea {
        font-size: 13px;
    }

    .react-tagsinput {
        background-color: var(--gray-0);
        border: 1px solid var(--gray-400);

        .react-tagsinput-input {
            background-color: var(--gray-200);
        }
    }
`

const CommentsArea = styled.textarea`
    min-height: 200px;
    height: 300px;
    min-height: 300px;
`

const Label = styled.label`
    font-size: 12px;

    > span {
        font-family: ${theme['font-regular']};
    }
`

const Section = styled.fieldset`
    margin-top: 13px;
    margin-bottom: 15px;

    .react-tagsinput--focused {
        border-color: ${theme['brand-primary']};
    }

    textarea,
    input {
        ::placeholder {
            color: #c1c6d0;
        }
    }

    .radio label {
        font-family: ${theme['font-regular']};
    }

    .form-group {
        margin: 0;
    }

    .Select.is-focused:not(.is-open) > .Select-control {
        border-color: ${theme['brand-primary']};
    }
`

const SectionTitle = styled.h3`
    font-family: ${theme['font-bold']};
    font-size: 14px;
    margin-bottom: 9px;
`

const borderError = `1px solid ${theme['text-danger']}`

const ActivityLogFormContainer = styled.div`
    padding: 0 20px;
    background: var(--gray-200);
    text-align: left;
`

export const MessengerForm = {
    get title() {
        return i18n('Registrar atividade').toUpperCase()
    },
    get editTitle() {
        return i18n('Editar registro de atividade').toUpperCase()
    },
    get shortTitle() {
        return i18n('Registros')
    },
    component: ActivityLogForm
}

// utils
function getUnixFromDateAndTime(mom: typeof moment, time: ?string) {
    time = time || ''
    time = time.replace(/_/g, '0')
    const m = moment(`${mom.format('YYYYMMDD')}${time}`, 'YYYYMMDDHH:mm:ss', true)

    return m.valueOf()
}

const promptCreator = text => `Adicionar opção ${text}`
const eventValueExtractor = e => e.target.value
const optionValueExtractor = option => ({ id: option.value, name: option.label })
const liveOnChangeSelectExtractor = (v, option) => optionValueExtractor(option)
const makeSelectList = (list, dataFields = ['id', 'name']) =>
    list.map(item => ({
        value: item.id,
        label: item.name,
        data: dataFields && _.pick(item, dataFields)
    }))
const makeSelectListFromRigs = (rigsList: AssetEntry[], dataFields) =>
    rigsList.map(item => ({
        value: item.name,
        label: item.name,
        data: item
    }))
